import * as React from 'react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import FolderIcon from '@mui/icons-material/Folder';
import Paper from '@mui/material/Paper';
import { DocumentHubTableDocuments } from './DocumentHubTableDocuments';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Typography } from '@mui/material';
import { Order, getComparator } from './tableUtills';
import { useState, useMemo } from 'react';
import { FolderTableRow } from '../../models/documentHub';

export interface HeadCell {
  disablePadding: boolean;
  id: keyof FolderTableRow;
  label: string;
  numeric: boolean;
}

const headCells: readonly HeadCell[] = [
  {
    id: 'folderName',
    numeric: false,
    disablePadding: false,
    label: 'Folder Name',
  },
  {
    id: 'createdOn',
    numeric: false,
    disablePadding: false,
    label: 'Date/Time Last Updated',
  },
  {
    id: 'createdBySub',
    numeric: false,
    disablePadding: false,
    label: 'Uploaded By',
  },
];

interface EnhancedTableProps {
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof FolderTableRow
  ) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler =
    (property: keyof FolderTableRow) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={'left'}
            padding={'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              key={`${headCell.id}-sort-label`}
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

interface EnhancedTableToolbarProps {
  folders?: FolderTableRow[];
  folderView: boolean;
  setFolderView: React.Dispatch<React.SetStateAction<boolean>>;
}

export function DocumentHubTableFolders(props: EnhancedTableToolbarProps) {
  const { folderView, setFolderView, folders } = props;

  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof FolderTableRow>('folderName');

  const handleRequestSort = (
    _event: React.MouseEvent<unknown>,
    property: keyof FolderTableRow
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const [selectedFolderName, setSelectedFolderName] =
    React.useState<string>('');
  const [selectedFolderKey, setSelectedFolderKey] = React.useState<string>('');

  const handleClick = (
    _event: React.MouseEvent<unknown>,
    row: FolderTableRow
  ) => {
    setSelectedFolderName(row.folderName);
    setSelectedFolderKey(row.folderKey);
    setFolderView(true);
  };

  const sortedFolders = useMemo(() => {
    if (!folders) return [];
    return [...folders].sort(getComparator(order, orderBy));
  }, [folders, order, orderBy]);

  return (
    <>
      {!folderView && folders ? (
        <Box sx={{ width: '100%' }} className="document-hub-table">
          <Paper
            sx={{
              width: '100%',
              mb: 2,
              boxShadow: 'unset',
            }}
          >
            <TableContainer
              sx={{
                border: 'solid 1px #EAECF0',
                borderBottom: '0',
              }}
            >
              <Table
                sx={{ minWidth: 750 }}
                aria-labelledby="tableTitle"
                size={'medium'}
              >
                <EnhancedTableHead
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleRequestSort}
                  rowCount={folders.length}
                />
                <TableBody>
                  {sortedFolders.map((row, index) => {
                    return (
                      <TableRow
                        hover
                        onClick={(event) => handleClick(event, row)}
                        key={index}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell align="left">
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <FolderIcon
                              sx={{
                                marginRight: '4px',
                                color: '#F2C36A66',
                                width: '33px',
                                height: '26px',
                              }}
                            />
                            {row.folderName}
                          </Box>
                        </TableCell>
                        <TableCell align="left">{row.createdOn}</TableCell>
                        <TableCell align="left">{row.createdBySub}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      ) : (
        <>
          <Box
            onClick={() => setFolderView(false)}
            sx={{ cursor: 'pointer', marginTop: '32px', display: 'flex' }}
          >
            <ArrowBackIcon sx={{ cursor: 'pointer' }} />
            <Typography>{selectedFolderName}</Typography>
          </Box>
          <DocumentHubTableDocuments folderType={selectedFolderKey} />
        </>
      )}
    </>
  );
}
